import { useQuery } from '@apollo/client';
import { FormField, Spinner, Text } from '@dixa/lib-shared-habla';
import { HookFormText } from 'lib-shared-hook-form';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DeepWritable } from 'ts-essentials';

import {
  PartnerSettingsConfiguration,
  PartnerSettingsConfigurationSchema,
  ReadPartnerSettingsConfigurationSchemaArg,
  readPartnerSettingsConfigurationSchemaQuery,
  ReadPartnerSettingsConfigurationSchemaResponse,
} from 'apis/partnerIntegrations';

import { ThirdPartySettingsConfigurationSchemaField } from './components/schemaField/thirdPartySettingsConfigurationSchemaField';

type ThirdPartySettingsConfigurationProps = {
  mode: 'create' | 'update';
  integrationType: string;
  initialValues?: PartnerSettingsConfiguration;
};

/**
 * A component for rendering the settings configuration form of a 3rd party
 * integration.
 *
 * @param mode - The mode of the form.
 * @param integrationType - The type of the integration.
 * @param initialValues - The initial values of the integration in case we want
 *   to update an existing integration.
 * @returns The form fields for the settings configuration.
 */
export const ThirdPartySettingsConfiguration = ({
  mode,
  integrationType,
  initialValues,
}: ThirdPartySettingsConfigurationProps): JSX.Element => {
  const form = useFormContext();
  const [schema, setSchema] = useState<PartnerSettingsConfigurationSchema[]>(
    [],
  );

  const isCreateMode = mode === 'create';
  const hasNoIntegrationToUpdate = !initialValues && mode === 'update';

  const { loading: isSchemaLoading, error: schemaError } = useQuery<
    ReadPartnerSettingsConfigurationSchemaResponse,
    ReadPartnerSettingsConfigurationSchemaArg
  >(readPartnerSettingsConfigurationSchemaQuery, {
    variables: {
      integrationType,
    },
    onCompleted(data) {
      if (data.Management.settingsConfigurationSchema.length > 0) {
        const schema = data.Management.settingsConfigurationSchema;

        setSchema(schema as DeepWritable<PartnerSettingsConfigurationSchema[]>);

        const formValues = schema.reduce(
          (acc, item) => ({
            ...acc,
            [item.key]: {
              key: item.key,
              secret: item.secret,
              value: initialValues?.settings.find(
                (setting) => setting.key === item.key,
              )?.value,
              // We have two scenarios here:
              // 1) When initializing
              //   - the setting is secret=true so we set isEditing=false and hide the input.
              //   - the setting is secret=false, so we set isEditing=true and show the input.
              // 2) We create a widget (creating=true), so we set isEditing=true in
              //    order to show the input.
              isEditing: isCreateMode ? true : !item.secret,
            },
          }),
          {},
        );

        form.reset({
          ...formValues,
          name: {
            value: initialValues?.name,
            seret: false,
          },
        });
      } else {
        form.reset({});
      }
    },
  });

  if (hasNoIntegrationToUpdate) {
    return (
      <FormField>
        <Text.Paragraph>
          Something went wrong, there is no integration to update. Please
          contact support.
        </Text.Paragraph>
      </FormField>
    );
  }

  if (schemaError) {
    return (
      <FormField>
        <Text.Paragraph>
          Something went wrong. Please try again later or contact support.
        </Text.Paragraph>
      </FormField>
    );
  }

  return isSchemaLoading ? (
    <Spinner />
  ) : (
    <>
      <FormField>
        <HookFormText
          label="Name"
          name="name.value"
          rules={{
            required: 'The name is required',
          }}
          componentProps={{
            placeholder: 'Enter a name for your integration',
          }}
        />
      </FormField>

      {schema.map((field) => (
        <FormField key={field.key}>
          <ThirdPartySettingsConfigurationSchemaField
            isCreateMode={isCreateMode}
            field={field}
          />
        </FormField>
      ))}
    </>
  );
};
